import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Trash2, Megaphone, UserSearch, Eye, MapPin, Phone, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { getStyle } from "../lib/mapStyles";
import { AttachmentPicker } from "./AttachmentPicker";
import { mediaUrl } from "../lib/api";
import { useMapFullscreen, MapFullscreenButton } from "./MapFullscreenToggle";
import { getCountryConfig } from "../lib/countryConfig";

const _ccMap = (getCountryConfig() || {}).map || {};
const BAMAKO = {
  lat: typeof _ccMap.citizen_center_lat === "number" ? _ccMap.citizen_center_lat : 12.6392,
  lng: typeof _ccMap.citizen_center_lng === "number" ? _ccMap.citizen_center_lng : -8.0029,
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch (_e) { return iso; } };

const SEVERITY = [
  { key: "info", label: "Info", color: "bg-sky-600" },
  { key: "warning", label: "Avertissement", color: "bg-amber-600" },
  { key: "critical", label: "Critique", color: "bg-rescue-red" },
];

const CIRCLE_SOURCE = "bc-radius-src";
const CIRCLE_FILL = "bc-radius-fill";
const CIRCLE_LINE = "bc-radius-line";

// Build a GeoJSON polygon approximating a circle of `radiusKm` km around [lng, lat].
const circleGeoJSON = ([lng, lat], radiusKm, points = 64) => {
  const km = radiusKm;
  const ret = [];
  const distanceX = km / (111.320 * Math.cos((lat * Math.PI) / 180));
  const distanceY = km / 110.574;
  for (let i = 0; i < points; i += 1) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    ret.push([lng + x, lat + y]);
  }
  ret.push(ret[0]);
  return { type: "Feature", geometry: { type: "Polygon", coordinates: [ret] } };
};

export const BroadcastsTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [point, setPoint] = useState({ lng: BAMAKO.lng, lat: BAMAKO.lat });
  const [form, setForm] = useState({ title: "", message: "", radius_km: 5, severity: "warning", expires_in_hours: 12 });
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Fullscreen toggle for the radius-picker map.
  const { isFs, toggle: toggleFs, fsWrapperClass } = useMapFullscreen(mapRef);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/broadcasts");
      setList(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { Promise.resolve().then(load); }, []);

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getStyle("dark"),
      center: [point.lng, point.lat],
      zoom: 5,
      attributionControl: false,
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;
    const ro = new ResizeObserver(() => { try { map.resize(); } catch (_e) { /* noop */ } });
    ro.observe(containerRef.current);

    map.on("load", () => {
      map.addSource(CIRCLE_SOURCE, {
        type: "geojson",
        data: circleGeoJSON([point.lng, point.lat], Number(form.radius_km)),
      });
      map.addLayer({
        id: CIRCLE_FILL,
        type: "fill",
        source: CIRCLE_SOURCE,
        paint: { "fill-color": "#CE1126", "fill-opacity": 0.15 },
      });
      map.addLayer({
        id: CIRCLE_LINE,
        type: "line",
        source: CIRCLE_SOURCE,
        paint: { "line-color": "#CE1126", "line-width": 2 },
      });
      const marker = new maplibregl.Marker({ color: "#CE1126", draggable: true })
        .setLngLat([point.lng, point.lat])
        .addTo(map);
      marker.on("dragend", () => {
        const ll = marker.getLngLat();
        setPoint({ lng: ll.lng, lat: ll.lat });
      });
      markerRef.current = marker;
    });

    map.on("click", (e) => {
      const ll = e.lngLat;
      setPoint({ lng: ll.lng, lat: ll.lat });
      markerRef.current?.setLngLat([ll.lng, ll.lat]);
    });

    return () => { ro.disconnect(); map.remove(); mapRef.current = null; markerRef.current = null; };
  }, []);

  // Update circle when point or radius change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource(CIRCLE_SOURCE);
      if (src) src.setData(circleGeoJSON([point.lng, point.lat], Number(form.radius_km) || 0.1));
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
    markerRef.current?.setLngLat([point.lng, point.lat]);
  }, [point, form.radius_km]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) return toast.error("Titre et message requis");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        latitude: point.lat,
        longitude: point.lng,
        radius_km: Number(form.radius_km),
        severity: form.severity,
        expires_in_hours: Number(form.expires_in_hours),
        attachments,
      };
      const { data } = await api.post("/admin/broadcasts", payload);
      setList((prev) => [data, ...prev]);
      setForm({ title: "", message: "", radius_km: 5, severity: "warning", expires_in_hours: 12 });
      setAttachments([]);
      toast.success("Diffusion envoyée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    try {
      await api.delete(`/admin/broadcasts/${id}`);
      setList((prev) => prev.map((b) => (b.id === id ? { ...b, active: false } : b)));
      toast.success("Désactivée");
    } catch (_e) { toast.error("Action impossible"); }
  };

  return (
    <div className="flex-1 px-3 sm:px-5 py-4 pb-8 space-y-4" data-testid="admin-broadcasts-tab">
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        <Megaphone className="w-4 h-4 text-mali-yellow" strokeWidth={2.4} />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">
          Centre de diffusion
        </span>
        <span className="ml-auto font-mono text-[10px] text-zinc-500 tabular-nums">
          <span className="text-emerald-300 font-bold">{list.length}</span> ACTIVE{list.length === 1 ? "" : "S"}
        </span>
      </div>

      <div className="border border-zinc-800 bg-[#0f1318]">
        <div className="bg-black border-b border-zinc-800 px-4 py-2 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-mali-yellow" strokeWidth={2.5} />
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-300">Composer une diffusion</span>
        </div>
        <form onSubmit={submit} className="p-3 space-y-2">
          <input
            data-testid="bc-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Titre (ex: Évacuation immédiate)"
            className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none px-3 py-2 text-sm"
          />
          <textarea
            data-testid="bc-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={3}
            placeholder="Message à diffuser aux citoyens dans la zone…"
            className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none px-3 py-2 text-sm resize-none"
          />

          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 pt-1">
            Zone : tapez sur la carte ou faites glisser le marqueur
          </div>
          <div
            className={fsWrapperClass("border border-zinc-700 relative")}
            style={isFs ? undefined : { height: 220 }}
            data-testid="broadcasts-map-container"
          >
            <div ref={containerRef} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }} />
            <MapFullscreenButton
              isFs={isFs}
              onToggle={toggleFs}
              className="absolute top-2 right-2 z-[450]"
            />
          </div>
          <div className="font-mono text-[10px] text-zinc-500 tabular-nums">
            Centre : {point.lat.toFixed(4)}, {point.lng.toFixed(4)} · Rayon : {form.radius_km} km
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            {SEVERITY.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`bc-sev-${s.key}`}
                onClick={() => setForm({ ...form, severity: s.key })}
                className={`py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors border ${
                  form.severity === s.key ? `${s.color} text-white border-transparent` : "border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Rayon (km)
              <input
                type="number" min="0.5" max="500" step="0.5"
                data-testid="bc-radius"
                value={form.radius_km}
                onChange={(e) => setForm({ ...form, radius_km: e.target.value })}
                className="mt-1 w-full border border-zinc-700 bg-black/40 text-zinc-100 px-2 py-1.5 text-sm font-mono focus:border-emerald-500 focus:outline-none"
              />
            </label>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Expire après (h)
              <input
                type="number" min="1" max="168"
                data-testid="bc-expires"
                value={form.expires_in_hours}
                onChange={(e) => setForm({ ...form, expires_in_hours: e.target.value })}
                className="mt-1 w-full border border-zinc-700 bg-black/40 text-zinc-100 px-2 py-1.5 text-sm font-mono focus:border-emerald-500 focus:outline-none"
              />
            </label>
          </div>

          <div className="border border-dashed border-amber-500/40 bg-amber-500/5 p-2 space-y-2">
            <div className="flex items-center gap-1.5 text-amber-300">
              <UserSearch className="w-3.5 h-3.5" strokeWidth={2.5} />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                Photos · personnes recherchées / scènes
              </span>
            </div>
            <AttachmentPicker
              attachments={attachments}
              onChange={setAttachments}
              testidPrefix="bc-attach"
              max={4}
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="bc-submit"
            className="w-full bg-rescue-red text-white py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-rose-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Send className="w-4 h-4" /> {saving ? "Envoi…" : "Diffuser"}
          </button>
        </form>
      </div>

      <div className="space-y-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
          {loading ? "Chargement…" : `${list.length} diffusion${list.length === 1 ? "" : "s"}`}
        </div>
        {list.map((b) => (
          <BroadcastListItem key={b.id} b={b} onDeactivate={deactivate} />
        ))}
      </div>
    </div>
  );
};

// ---------------------- Sightings panel (per-broadcast, lazy-loaded) ----------------------
const BroadcastListItem = ({ b, onDeactivate }) => {
  const sev = SEVERITY.find((s) => s.key === b.severity) || SEVERITY[1];
  const [count, setCount] = useState(null);
  const [open, setOpen] = useState(false);
  const [sightings, setSightings] = useState([]);
  const [loadingSightings, setLoadingSightings] = useState(false);

  // Fetch sighting count once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/broadcasts/${b.id}/sightings/count`);
        setCount(data.count || 0);
      } catch (_e) { setCount(0); }
    })();
  }, [b.id]);

  const toggle = async () => {
    if (!open && sightings.length === 0 && (count || 0) > 0) {
      setLoadingSightings(true);
      try {
        const { data } = await api.get(`/admin/broadcasts/${b.id}/sightings`);
        setSightings(data || []);
      } catch (err) {
        // Surface fetch errors so dispatchers see when the API is down
        // instead of an empty list being mistaken for "no sightings".
        console.error(`BroadcastsTab: failed to load sightings for ${b.id}`, err);
      } finally { setLoadingSightings(false); }
    }
    setOpen(!open);
  };

  return (
    <div className={`border ${b.active ? "border-zinc-800" : "border-zinc-800/50 opacity-60"} bg-[#0f1318] p-3`} data-testid={`bc-item-${b.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`${sev.color} text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em]`}>{sev.label}</div>
        {b.active && (
          <button
            onClick={() => onDeactivate(b.id)}
            data-testid={`bc-deactivate-${b.id}`}
            className="text-[10px] uppercase tracking-[0.15em] text-rose-300 font-bold hover:underline flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Désactiver
          </button>
        )}
      </div>
      <div className="font-display text-base font-black mt-1 text-zinc-100">{b.title}</div>
      <p className="text-xs text-zinc-400 mt-0.5">{b.message}</p>
      {Array.isArray(b.attachments) && b.attachments.length > 0 && (
        <div className="flex gap-1.5 mt-2 overflow-x-auto cmd-scroll-row" data-testid={`bc-attachments-${b.id}`}>
          {b.attachments.map((a, idx) => (
            <a
              key={`${a.url}-${idx}`}
              href={mediaUrl(a.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 w-14 h-14 border border-zinc-700 bg-black overflow-hidden block"
              title={a.name}
            >
              {a.kind === "image" ? (
                <img src={mediaUrl(a.url)} alt={a.name || ""} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[9px] font-bold uppercase text-zinc-500">
                  {a.kind}
                </div>
              )}
            </a>
          ))}
        </div>
      )}
      <div className="text-[10px] text-zinc-500 mt-1 font-mono tabular-nums">
        {b.latitude.toFixed(3)}, {b.longitude.toFixed(3)} · rayon {b.radius_km} km · expire {fmt(b.expires_at)}
      </div>

      {/* Sightings counter / accordion */}
      <button
        type="button"
        onClick={toggle}
        data-testid={`bc-sightings-toggle-${b.id}`}
        className={`mt-2 w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-colors border ${
          (count || 0) > 0 ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25" : "border-zinc-700 text-zinc-500"
        }`}
      >
        <span className="flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" />
          {count === null ? "Chargement…" : `${count} signalement${count === 1 ? "" : "s"} de témoin`}
        </span>
        {(count || 0) > 0 && (open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`bc-sightings-list-${b.id}`}>
          {loadingSightings && <div className="text-[10px] text-zinc-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Chargement…</div>}
          {sightings.map((s) => (
            <div key={s.id} className="border-l-2 border-emerald-500 bg-emerald-500/5 p-2" data-testid={`bc-sighting-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] font-mono text-zinc-500">{fmt(s.created_at)}</div>
                {s.reporter_phone && (
                  <a
                    href={`tel:${s.reporter_phone}`}
                    className="flex items-center gap-1 text-[10px] font-bold text-emerald-300 hover:underline"
                  >
                    <Phone className="w-3 h-3" /> {s.reporter_phone}
                  </a>
                )}
              </div>
              {s.description && <p className="text-xs text-zinc-200 mt-1 leading-snug">{s.description}</p>}
              {s.coordinates && (
                <a
                  href={`https://www.google.com/maps?q=${s.coordinates.latitude},${s.coordinates.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300 hover:underline font-mono tabular-nums"
                >
                  <MapPin className="w-3 h-3" />
                  {s.coordinates.latitude.toFixed(4)}, {s.coordinates.longitude.toFixed(4)}
                </a>
              )}
              {Array.isArray(s.attachments) && s.attachments.length > 0 && (
                <div className="flex gap-1.5 mt-1.5 overflow-x-auto cmd-scroll-row">
                  {s.attachments.map((a, idx) => (
                    <a key={`${a.url}-${idx}`} href={mediaUrl(a.url)} target="_blank" rel="noopener noreferrer" className="shrink-0 w-10 h-10 border border-zinc-700 bg-black overflow-hidden">
                      {a.kind === "image" ? <img src={mediaUrl(a.url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[8px] font-bold uppercase text-zinc-500">{a.kind}</div>}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!loadingSightings && sightings.length === 0 && (
            <div className="text-[10px] text-zinc-500 text-center py-2">Aucun signalement pour l&apos;instant.</div>
          )}
        </div>
      )}
    </div>
  );
};
